import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { capitalize } from 'radash'
import InlineProfile from '~components/general/inline-profile'
import MediaList from '~components/general/media-list'
import PetCard from '~components/general/pet-card'
import AvatarPet from '~components/ui/avatar/avatar-pet'
import Button from '~components/ui/button'
import Icon from '~components/ui/icon'
import SanitizedText from '~components/ui/sanitized-text'
import { AVATAR_DEFAULT } from '~constants'
import NotFound404 from '~containers/general/404'
import Loading from '~containers/general/loading'
import Pet from '~models/pet'
import { State } from '~models/consultation/types'
import useConsultationDetail from '~repositories/hooks/use-consultation-detail'
import useMyProfile from '~repositories/hooks/use-my-profile'
import getRootPath from '~utils/app/get-root-path'
import { cn } from '~utils/cn'
import { convertIsoToReadable } from '~utils/date'
import convertPetAge from '~utils/pet/convert-pet-age'
import { convertPetGender } from '~utils/pet/convert-pet-gender'

import { Conversation, SmartSearch, VetAnswer, WaitingAnswerLottieBlock } from './components'
import CherryPickedConsultations from './components/cherry-picked-consultations'
import ConcernLevelBlock from './components/concern-level-block'
import VetFinderCard from './components/vet-finder-card'
import { IssueDetails, IssueDetailsSlider } from './components/issue-details'
import Mixpanel from '~utils/mixpanel'
import { useEffect } from 'react'

type ConsultationDetailPageProps = Readonly<{
  id: string | number
}>

type ListItemProps = Readonly<{
  title: string
  value: string
  type?: 'row' | 'col'
}>

type PetInfoProps = Readonly<{
  petInfo: [string, string][]
  pet: Pet.Level1
}>

type SubInfoTitleProps = Readonly<{ children: string }>

const ListItem = ({ title, value, type = 'row' }: ListItemProps) => {
  return (
    <div
      className={cn('flex border-b border-solid border-gray-200 p-1 last:border-b-0', {
        'flex-col gap-1': type === 'col',
        'items-center justify-between py-2': type === 'row',
      })}
    >
      <div className="text-gray-500 text-label-w-1">{title}</div>
      <div className="text-gray-900 text-label-3">{value}</div>
    </div>
  )
}

const PetInfo = ({ pet, petInfo }: PetInfoProps) => {
  return (
    <div className="rounded-xl bg-gray-50 p-3">
      <div className="flex justify-center py-2">
        <AvatarPet pet={pet} size="64" />
      </div>
      {petInfo.map(([title, value]) => (
        <ListItem key={title} title={title} value={value} type="row" />
      ))}
    </div>
  )
}

const SubInfoTitle = ({ children }: SubInfoTitleProps) => (
  <h2 className="p-5 pb-2 text-gray-900 text-label-2">{children}</h2>
)

const Divider = () => <hr className="h-2 lg:h-[1px] border-none bg-gray-300 dark:bg-gray-900" />

const AskAVetGuideBlock = () => {
  return (
    <div className="flex flex-col justify-between flex-gap-y-5 rounded-[20px] bg-extended-alabaster px-4 py-5 lg:flex-row lg:items-center">
      <div className="flex items-center flex-gap-x-4">
        <img
          src={`${import.meta.env.VITE_STATIC_RES}/img/illustration_askavet.webp`}
          width={64}
          height={64}
          alt="illustration_askavet"
        />
        <p className="whitespace-pre-line text-primary-600 text-title-3">
          {`Having similar issues?\nAsk a Dr.Tail Vet what concerns you and gain peace of mind.`}
        </p>
      </div>
      <Button size="medium" className="w-full shrink-0 lg:w-fit">
        Ask a Dr.Tail Vet now
      </Button>
    </div>
  )
}

const ClosedNoticeBlock = () => {
  const navigate = useNavigate()

  // 새 상담 시작 (ask-a-vet 시작 경로 재사용)
  const handleStartNew = () => {
    navigate({ to: `${getRootPath()}/ask-a-vet`, resetScroll: true })
  }

  return (
    <div className="flex flex-col flex-gap-y-4 rounded-2xl bg-gray-50 px-4 py-5 dark:bg-gray-900">
      <p className="text-gray-900 text-body-2 dark:text-gray-300">
        This consultation is closed. For a new or ongoing concern, please start a new consultation.
      </p>
      <Button size="medium" className="w-full" onClick={handleStartNew}>
        Start a new consultation
      </Button>
    </div>
  )
}

const ConsultationDetail = ({ id }: ConsultationDetailPageProps) => {
  const { data: consultation, isPending } = useConsultationDetail({ id })
  const { data: myProfile } = useMyProfile()

  const owner = consultation?.owner
  const ownerInfo = {
    thumbnailUri: owner?.thumbnail_uri || AVATAR_DEFAULT.OWNER,
    nickname: owner?.nickname || 'Unknown',
  }

  const isMyConsultation = owner?.id === myProfile?.id

  const initialQuestion = consultation?.conversation.at(0)
  const initialAnswer = consultation?.conversation.at(1)
  const conversation = consultation?.conversation.slice(2) // 질문 본문 및 첫 답변 제외

  const videoUrls = initialQuestion?.video_urls
  const photoUrls = initialQuestion?.photo_urls
  const hasMediaFiles = !!videoUrls?.length || !!photoUrls?.length

  const vet = consultation?.vet
  const vetInfo = {
    thumbnailUri: vet?.photo_url || AVATAR_DEFAULT.VET,
    nickname: vet?.title || 'Dr.Tail Vet',
  }

  const mainConcern = capitalize(consultation?.main_concern || '')

  const checkupSummary = consultation?.checkup_summary || {}
  const checkupData =
    consultation?.main_concern && consultation?.checkup_data
      ? Object.entries({
          'Main Concern': mainConcern,
          ...consultation.checkup_data,
        })
      : []

  const pet = consultation?.pet
  const petInfo = Object.entries({
    Name: pet?.name || 'Unknown',
    Species: pet?.kind?.depth1 || 'Unknown',
    Breed: pet?.kind?.depth2 || 'Unknown',
    BirthDay: pet?.born_at ? convertIsoToReadable(pet.born_at) : 'Unknown',
    Age: pet?.born_at ? convertPetAge(pet.born_at) : 'Unknown',
    Gender: convertPetGender(pet?.gender),
    Status: pet?.is_neutered ? 'Neutered or spayed' : 'Intact',
  })

  const isLoading = isPending

  if (isLoading) {
    return <Loading />
  }

  if (!consultation) {
    return <NotFound404 />
  }

  useEffect(() => {
    Mixpanel.logEvent('askavet: checked', {
      consultation_id: consultation.id,
      is_user_scenario: true,
    })
  }, [])

  return (
    <article className="no-scrollbar min-h-screen w-full pb-10">
      <div className="mt-2 lg:mt-8 lg:flex lg:items-start lg:justify-center lg:flex-gap-x-5">
        <div className="w-full lg:p-5">
          <div className="flex items-center flex-gap-x-2 px-5 h-[64px]">
            <Icon name="question_sm" className="size-6 fill-gray-800 dark:fill-white" />
            <div className="text-gray-900 text-headline-3 lg:text-headline-w-2 dark:text-white">
              My Question
            </div>
          </div>
          <div className="px-5 py-2">
            <InlineProfile size="lg">
              <InlineProfile.Avatar thumbnailUri={ownerInfo.thumbnailUri} />
              <InlineProfile.Header
                nickname={ownerInfo.nickname}
                createdAt={dayjs(consultation?.created_at).format('MMM DD, YYYY')}
              />
            </InlineProfile>
          </div>
          {consultation?.details && (
            <p className="px-5 py-2 text-gray-900 text-body-2 lg:text-body-3 dark:text-gray-300">
              <SanitizedText text={consultation.details} />
            </p>
          )}
          {hasMediaFiles && (
            <div className="p-5 pr-0">
              <MediaList videoUrls={videoUrls} photoUrls={photoUrls} />
            </div>
          )}
          {consultation?.pet && (
            <div className="px-5 pb-5 lg:hidden">
              <PetCard pet={pet} className="flex flex-gap-x-3 bg-gray-50 dark:bg-gray-900">
                <PetCard.Avatar />
                <div className="min-w-0">
                  <PetCard.Name />
                  <PetCard.Kind />
                  <PetCard.AgeWithGender />
                </div>
              </PetCard>
            </div>
          )}
          {!!checkupData?.length && (
            <div className="mb-5 overflow-x-hidden py-3">
              <IssueDetailsSlider checkupSummary={checkupSummary} checkupData={checkupData} />
            </div>
          )}
          {consultation.concern_lv &&
            consultation.concern_lv_expression &&
            consultation.concern_lv_description &&
            pet && (
              <section className="flex flex-col px-5 mb-6 w-full">
                <div className="flex items-center flex-gap-x-2 h-[64px]">
                  <Icon name="level_sm" className="size-6 fill-gray-800 dark:fill-white" />
                  <div className="text-gray-900 text-headline-3 lg:text-headline-w-2 dark:text-white">
                    Emergency Level
                  </div>
                </div>
                <ConcernLevelBlock
                  concern_lv={consultation.concern_lv}
                  concern_lv_expression={consultation.concern_lv_expression}
                  concern_lv_description={consultation.concern_lv_description}
                  pet={pet}
                />
              </section>
            )}
          {/* Find a vet (DRT-7239): server-driven, only for the owner and only when rolled out */}
          {isMyConsultation && consultation.vet_finder && (
            <div className="px-5 mb-6">
              <VetFinderCard vetFinder={consultation.vet_finder} consultationId={consultation.id} />
            </div>
          )}
          <div className={cn('relative', isMyConsultation && initialAnswer)}>
            <div className="flex items-center flex-gap-x-2 px-5 h-[64px]">
              <Icon name="answer_sm" className="size-6 fill-gray-800 dark:fill-white" />
              <div className="text-gray-900 text-headline-3 lg:text-headline-w-2 dark:text-white">
                Answer
              </div>
            </div>
            {initialAnswer ? (
              <VetAnswer
                vet={vet}
                answer={initialAnswer.text}
                createdAt={initialAnswer.created_at}
              />
            ) : isMyConsultation && !initialAnswer ? (
              <WaitingAnswerLottieBlock createdAt={consultation.created_at} />
            ) : null}
            {!isMyConsultation && (
              <div className="p-5">
                <AskAVetGuideBlock />
              </div>
            )}
            <Conversation conversation={conversation} ownerInfo={ownerInfo} vetInfo={vetInfo} />
            {isMyConsultation && consultation.state === State.Closed && (
              <div className="p-5">
                <ClosedNoticeBlock />
              </div>
            )}
          </div>
          {!isMyConsultation && consultation?.id && (
            <CherryPickedConsultations id={consultation.id} />
          )}
          {isMyConsultation && (
            <div className="mt-8 lg:mt-0">
              <Divider />
              <div className="px-6 py-[28px] lg:px-5 lg:py-10">
                <SmartSearch id={consultation.id} />
              </div>
            </div>
          )}
        </div>
        <div className="hidden w-full max-w-[300px]">
          {!!checkupData?.length && (
            <>
              <SubInfoTitle>Issue details</SubInfoTitle>
              <div className="px-5 py-2">
                <div className="rounded-2xl bg-gray-50 p-3">
                  <IssueDetails checkupData={checkupData} />
                </div>
              </div>
            </>
          )}
          <SubInfoTitle>Pet information</SubInfoTitle>
          {pet && (
            <div className="px-5 py-1">
              <PetInfo pet={pet} petInfo={petInfo} />
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

export default ConsultationDetail
